import { useMemo, useState, useCallback } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { PageContainer } from '../components/layout';
import { AskOrSearch } from '../components/AskOrSearch';
import type { ChatMessage, ChatResponse } from '../components/AskOrSearch';
import {
  createAdvisorTransport,
  acquireAdvisorToken,
  acquireGraphToken,
  AdvisorAuthError,
} from '../services/advisorClient';
import { LIBRARY_UNREACHABLE_NOTICE } from '../services/advisorIndex';
import { isPracticeMode } from '../services/commitAdapter';
import { isLiveRef, isPracticeRef, parsePracticeRef, parseRef } from '../lib/recordQuery';
import styles from './LookupPage.module.css';

/**
 * LookupPage (05-10) — the office entry point of the "one engine, two entry
 * points" policy & process lookup buddy (05-CONTEXT).
 *
 * Hosts the shared AskOrSearch component (a verbatim COPY of the fieldlink
 * folder) with the OFFICE transport injected: the signed-in user's own Entra
 * token drives the Q&S Advisor loop client-side, library search reads the
 * Policy + Process Registers over delegated Graph, and citations open the
 * actual SP document directly (office staff have Q&S-site access — no proxy,
 * no watermark). All App Roles see this page: policy/process lookup is
 * universal, so there is no scope filtering here.
 *
 * 13-25: a register reference typed here (`INC-12`, `CMP-3`, …) opens that record's page (`/record/<ref>`); a
 * practice ref (`INC-P1`) only in practice mode. The record page sends an unknown reference back here.
 *
 * Until the 05-11 Entra grant (dashboard app → Advisor API permission +
 * admin consent + Advisor FA CORS) is provisioned, Ask fails with
 * AdvisorAuthError — surfaced here as an honest banner while document search
 * (Graph-only) keeps working.
 */
/** A register reference typed into Lookup → its canonical form, or `null`. Practice refs only in practice mode. */
function recordRefOf(text: string): string | null {
  const t = text.trim();
  if (isLiveRef(t)) return parseRef(t).ref;
  if (isPracticeMode() && isPracticeRef(t)) return parsePracticeRef(t).ref;
  return null;
}

/** Open a record by its reference (13-25). */
function OpenRecord() {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [refused, setRefused] = useState(false);
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const ref = recordRefOf(text);
    if (ref) navigate(`/record/${ref}`);
    else setRefused(true);
  };
  return (
    <form className={styles.openRecord} onSubmit={onSubmit} role="search" aria-label="Open a record by its reference">
      <label htmlFor="lookup-record-ref" className={styles.openLabel}>
        Open a register record
      </label>
      <div className={styles.openRow}>
        <input
          id="lookup-record-ref"
          className={styles.openInput}
          value={text}
          placeholder="e.g. INC-12, CMP-3, FB-8, RSK-4, CI-21"
          autoComplete="off"
          aria-describedby={refused ? 'lookup-record-ref-msg' : undefined}
          onChange={(e) => {
            setText(e.target.value);
            setRefused(false);
          }}
        />
        <button type="submit" className={styles.openButton}>
          Open
        </button>
      </div>
      {refused && (
        <p id="lookup-record-ref-msg" className={styles.openRefused}>
          That isn't a register reference. Use the prefix and number, like INC-12.
        </p>
      )}
    </form>
  );
}

export default function LookupPage() {
  const { instance } = useMsal();
  const [advisorBlocked, setAdvisorBlocked] = useState(false);
  // ISS-229 b (13.1-10): our policy library couldn't be read for this account,
  // so no row opens our copy. Said plainly, never a silent Centro open.
  const [libraryUnreachable, setLibraryUnreachable] = useState(false);

  // The transport resolves the signed-in account from the MSAL instance at
  // call time, so `instance` (stable for the app's life) is the only dep.
  const transport = useMemo(
    () =>
      createAdvisorTransport({
        getAdvisorToken: () => acquireAdvisorToken(instance),
        getGraphToken: () => acquireGraphToken(instance),
        onLibraryStatus: (status) => setLibraryUnreachable(!status.reachable),
      }),
    [instance],
  );

  const postChat = useCallback(
    async (messages: ChatMessage[]): Promise<ChatResponse> => {
      try {
        const response = await transport.postChat(messages);
        setAdvisorBlocked(false);
        return response;
      } catch (err) {
        if (err instanceof AdvisorAuthError) setAdvisorBlocked(true);
        throw err;
      }
    },
    [transport],
  );

  return (
    <PageContainer title="Lookup" maxWidth="md">
      <p className={styles.intro}>
        Ask a question about how we work — or search for a policy or process by
        name. Answers come from Harmony&rsquo;s own policies and processes, with
        the source documents cited so you can open the real thing.
      </p>
      {libraryUnreachable && (
        <p className={styles.libraryNotice} role="status">
          {LIBRARY_UNREACHABLE_NOTICE}
        </p>
      )}
      {advisorBlocked && (
        <p className={styles.advisorBanner} role="alert">
          Ask isn&rsquo;t switched on for the dashboard yet — the Advisor access
          grant is still being provisioned (plan 05-11). Document search below
          still works.
        </p>
      )}
      <OpenRecord />
      <AskOrSearch
        postChat={postChat}
        searchLibrary={transport.searchLibrary}
        openDoc={transport.openDoc}
        placeholder="Ask a question or search policies and processes…"
      />
    </PageContainer>
  );
}
